import { useState, useEffect, useRef, useCallback } from 'react';
import { getProgressDashboardAggregate } from '../../Training/training-dashboard/services/trainingService';
import { prepareCoverageByModuleData, prepareProgressTrendsByLocationData, prepareProgressTrendsByPeriodsData } from '../utils/progressUtills';
import { computeLocalMetrics } from '../utils/dashboardMetrics';
import { apiCall } from '../../../_services/apiCall';
import config from "../../../config/config";

/**
 * Orchestrates every server-driven metric on the dashboard: the KPI/coverage/monthly-activity
 * payload, the auditor workload, the data-owner matrix, and the two progress-trend charts.
 *
 * A single aggregate API call builds the shared context server-side and returns each widget
 * slice together; the slice is stored in a ref so the trend loaders read from it (falling back
 * to a client-side computation when a slice is null — identical to the pre-aggregate behavior).
 */
export const useDashboardMetrics = ({
  financialYearId,
  selectedPeriods,
  selectedPeriodsValue,
  selectedLocations,
  selectedModules,
  aggregation,
  locationOptions,
  periodOptions,
  locations,
  frameworkOptions,
  moduleOptions,
  reportingQuestions,
  reportingAnswers,
  reportingAssignDetails,
  allUsers,
  setLoading,
  setError,
  userResult,
  filteredUsers,
  locationMap,
  mergedFrameworkAnswers,
  filteredQuestionsForProgressTrends,
  filteredQuestionsForProgressLocation,
}) => {
  const [progressTrendsByLocationData, setProgressTrendsByLocationData] = useState(null);
  const [progressTrendsByPeriodsData, setProgressTrendsByPeriodsData] = useState(null);
  const [loadingProgressTrendsByLocation, setLoadingProgressTrendsByLocation] = useState(false);
  const [loadingProgressTrendsByPeriods, setLoadingProgressTrendsByPeriods] = useState(false);
  const [teamWorksloadData, setTeamWorksloadData] = useState([]);
  const [auditorWorkloadData, setAuditorWorkloadData] = useState([]);
  const [dataOwnerMatrixData, setDataOwnerMatrixData] = useState(null);
  const [loadingDataOwnerMatrix, setLoadingDataOwnerMatrix] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalDataPoints: 0,
      overallCompletion: 0,
      activeContributors: 0,
      pendingReviews: 0
    },
    monthlyActivities: [],
    frameworks: [],
    locationCompletionChart: { series: [], categories: [] },
    periodCompletionChart: { series: [], categories: [] }
  });

  // Holds the latest aggregate payload so the trend loaders read their slice from it
  // instead of hitting a (now-removed) per-widget endpoint.
  const aggregateResultRef = useRef(null);
  const lastParamsRef = useRef(null);

  // Monotonic fetch id; each initializeData run captures its own and drops its
  // result if a newer run has started, so a slow older response never wins.
  const fetchIdRef = useRef(0);

  const loadProgressTrendsByLocation = useCallback(async () => {
    if (!financialYearId) return;
    setLoadingProgressTrendsByLocation(true);
    const data = aggregateResultRef.current?.progressTrendsByLocation;
    if (data) {
      setProgressTrendsByLocationData(data);
    } else {
      const fallbackData = prepareProgressTrendsByLocationData(
        selectedLocations.length === 0 ? locations.map(l => l.id) : selectedLocations,
        selectedPeriodsValue,
        filteredQuestionsForProgressLocation,
        mergedFrameworkAnswers,
        filteredUsers,
        userResult,
        locationMap,
        selectedPeriods
      );
      setProgressTrendsByLocationData(fallbackData);
    }
    setLoadingProgressTrendsByLocation(false);
  }, [financialYearId, selectedPeriodsValue, selectedLocations, selectedModules, locations, filteredQuestionsForProgressLocation, mergedFrameworkAnswers, filteredUsers, userResult, locationMap, selectedPeriods]);

  const loadProgressTrendsByPeriods = useCallback(async () => {
    if (!financialYearId) return;
    setLoadingProgressTrendsByPeriods(true);
    const data = aggregateResultRef.current?.progressTrendsByPeriods;
    if (data) {
      setProgressTrendsByPeriodsData(data);
    } else {
      const fallbackData = prepareProgressTrendsByPeriodsData(
        selectedPeriodsValue,
        filteredQuestionsForProgressTrends,
        mergedFrameworkAnswers,
        filteredUsers
      );
      setProgressTrendsByPeriodsData(fallbackData);
    }
    setLoadingProgressTrendsByPeriods(false);
  }, [financialYearId, selectedPeriodsValue, selectedLocations, selectedModules, filteredQuestionsForProgressTrends, mergedFrameworkAnswers, filteredUsers]);

  // Reset loaded chart states on filter changes so they refetch/recalculate
  useEffect(() => {
    setProgressTrendsByLocationData(null);
    setProgressTrendsByPeriodsData(null);
    setAuditorWorkloadData([]);
    setDataOwnerMatrixData(null);
  }, [financialYearId, selectedPeriodsValue, selectedLocations, selectedModules, locations, reportingQuestions, reportingAnswers, allUsers]);

  const teamWorkloadProgress = async (periods, financialYearId, frameworkIds, locationIdsIds, moduleIds) => {
    const currentParams = JSON.stringify({
      periods,
      financialYearId,
      frameworkIds,
      locationIdsIds,
      multiplier: periodOptions.length,
      moduleIds
    });

    if (lastParamsRef.current === currentParams) {
      return;
    }

    lastParamsRef.current = currentParams;
    const finalArr = moduleIds.every(v => v === null || v === undefined || v === "") ? [] : moduleIds;


    if (periods.length && financialYearId && frameworkIds.length && locationIdsIds.length && finalArr.length) {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}teamWorkloadProgress`,
        {},
        {
          financialYearId,
          frameworkIds,
          locationIdsIds,
          periods,
          multiplier: periodOptions.length,
          moduleIds
        },
        "GET"
      );

      if (isSuccess) {
        setTeamWorksloadData(data?.data?.teamWorkloadResults);
      }
    }
  };

  const fetchDataOwnerMatrix = useCallback(async () => {
    if (!financialYearId || dataOwnerMatrixData || loadingDataOwnerMatrix) return;
    setLoadingDataOwnerMatrix(true);
    try {
      const aggregate = await getProgressDashboardAggregate(
        financialYearId,
        selectedPeriodsValue,
        selectedLocations,
        selectedModules,
        aggregation,
        frameworkOptions.map(f => f.value),
        ['dataOwnerMatrix']
      );
      if (aggregate?.dataOwnerMatrix) {
        setDataOwnerMatrixData(aggregate.dataOwnerMatrix);
      }
    } catch (err) {
      console.error("Error loading matrix:", err);
    } finally {
      setLoadingDataOwnerMatrix(false);
    }
  }, [financialYearId, selectedPeriodsValue, selectedLocations, selectedModules, aggregation, frameworkOptions, loadingDataOwnerMatrix, dataOwnerMatrixData]);

  useEffect(() => {
    const initializeData = async () => {
      if (!financialYearId) return;
      const fetchId = ++fetchIdRef.current;
      try {
        const {
          filteredQuestions,
          mergedFrameworkAnswers,
          filteredUsers,
          fromDates,
          totalQuestions,
          overallCompletion,
          totalActiveUsers,
          totalPending,
          monthlyActivities,
        } = computeLocalMetrics({
          reportingQuestions,
          allUsers,
          selectedLocations,
          selectedModules,
          locationOptions,
          periodOptions,
          selectedPeriodsValue,
          reportingAnswers,
          locations,
          reportingAssignDetails,
        });

        // ── Phase 1: Fast initial load (summary + coverageByModule only) ──────────────
        const initialAggregate = await getProgressDashboardAggregate(
          financialYearId,
          selectedPeriodsValue,
          selectedLocations,
          selectedModules,
          aggregation,
          frameworkOptions.map(f => f.value),
          ['summary', 'coverageByModule']
        );
        if (fetchId !== fetchIdRef.current) return;
        aggregateResultRef.current = initialAggregate;

        let coverageByModuleData = initialAggregate?.coverageByModule;
        if (!coverageByModuleData) {
          coverageByModuleData = prepareCoverageByModuleData(
            selectedModules.length === 0 ? moduleOptions.map(m => m.id) : selectedModules,
            moduleOptions,
            filteredQuestions,
            mergedFrameworkAnswers,
            filteredUsers,
            fromDates
          );
        }

        let metricsOverride = null;
        const summary = initialAggregate?.summary;
        if (summary) {
          metricsOverride = {
            totalDataPoints: summary.totalDataPoints,
            overallCompletion: summary.overallCompletion,
            activeContributors: summary.activeContributors,
            pendingReviews: summary.pendingReviews
          };
        }

        setDashboardData(prev => ({
          ...prev,
          metrics: metricsOverride || {
            totalDataPoints: totalQuestions,
            overallCompletion: overallCompletion,
            activeContributors: totalActiveUsers,
            pendingReviews: totalPending
          },
          monthlyActivities: monthlyActivities,
          coverageByModule: coverageByModuleData,
        }));

        setLoading(false); // Unblock main page immediately!

        // ── Phase 2: Secondary load (trends + auditor workload) ─────────────────────
        setLoadingProgressTrendsByLocation(true);
        setLoadingProgressTrendsByPeriods(true);

        const trendsAggregate = await getProgressDashboardAggregate(
          financialYearId,
          selectedPeriodsValue,
          selectedLocations,
          selectedModules,
          aggregation,
          frameworkOptions.map(f => f.value),
          ['progressTrendsByLocation', 'progressTrendsByPeriods', 'auditorWorkload']
        );
        if (fetchId !== fetchIdRef.current) return;
        aggregateResultRef.current = { ...aggregateResultRef.current, ...trendsAggregate };

        if (trendsAggregate?.auditorWorkload) {
          setAuditorWorkloadData(trendsAggregate.auditorWorkload);
        }

        const frameworkIdsForAPI = frameworkOptions.map(f => f.value);
        const locationIdsForAPI = selectedLocations.length === 0 ? locations.map(l => l.id) : selectedLocations;
        const moduleIds = selectedModules.length === 0 ? moduleOptions.map(m => m.value).filter(m => m != null) : selectedModules.filter(m => m != null);
        teamWorkloadProgress(fromDates, financialYearId, frameworkIdsForAPI, locationIdsForAPI, moduleIds);

        await loadProgressTrendsByLocation();
        await loadProgressTrendsByPeriods();

      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        setError(err.message);
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
          setLoadingProgressTrendsByLocation(false);
          setLoadingProgressTrendsByPeriods(false);
        }
      }
    };

    initializeData();
  }, [selectedPeriodsValue, financialYearId, selectedLocations, selectedModules, allUsers, frameworkOptions, aggregation, loadProgressTrendsByLocation, loadProgressTrendsByPeriods]);

  return {
    progressTrendsByLocationData,
    progressTrendsByPeriodsData,
    loadingProgressTrendsByLocation,
    loadingProgressTrendsByPeriods,
    teamWorksloadData,
    auditorWorkloadData,
    dataOwnerMatrixData,
    loadingDataOwnerMatrix,
    fetchDataOwnerMatrix,
    dashboardData,
  };
};
